import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    IconButton,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    ZoomIn as ZoomInIcon,
    ZoomOut as ZoomOutIcon,
    RotateLeft as RotateLeftIcon,
    RotateRight as RotateRightIcon,
    Description as DescriptionIcon
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || '';

const DocumentPreview = ({ expenseId, extractedData }) => {
    const [rotation, setRotation] = useState(0);
    const [zoom, setZoom] = useState(1);
    const [documentUrl, setDocumentUrl] = useState(null);
    const [documentType, setDocumentType] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Only fetch document URL if we have an expense ID
        if (!expenseId) {
            setLoading(false);
            return;
        }

        const fetchDocumentUrl = async () => {
            try {
                setLoading(true);

                const response = await fetch(`${API_URL}/api/expenses/${expenseId}/document`, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch document');
                }

                const data = await response.json();

                // Set document URL and type from response
                setDocumentUrl(data.url);
                setDocumentType(data.type);
            } catch (err) {
                console.error('Error fetching document:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchDocumentUrl();

        // Cleanup function - important for security with signed URLs
        return () => {
            if (documentUrl) {
                // Nothing to revoke since using S3 signed URLs
                setDocumentUrl(null);
            }
        };
    }, [expenseId]);

    // Function to check document status
    const checkDocumentStatus = async () => {
        if (!expenseId) return;

        try {
            const response = await fetch(`${API_URL}/api/expenses/${expenseId}/status`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to check document status');
            }

            const data = await response.json();

            return data.status;
        } catch (err) {
            console.error('Error checking document status:', err);
            return 'error';
        }
    };

    // Render the document preview based on type
    const renderDocumentPreview = () => {
        if (!documentUrl) {
            return (
                <Typography color="textSecondary">
                    Nie można wyświetlić podglądu
                </Typography>
            );
        }

        if (documentType === 'application/pdf') {
            return (
                <iframe
                    src={documentUrl}
                    title="PDF Preview"
                    style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        backgroundColor: 'white'
                    }}
                />
            );
        } else if (documentType.startsWith('image/')) {
            return (
                <img
                    src={documentUrl}
                    alt="Document preview"
                    style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        transform: `rotate(${rotation}deg) scale(${zoom})`,
                        transition: 'transform 0.3s ease'
                    }}
                    onError={(e) => {
                        console.error('Error loading image:', e);
                        setError('Nie można załadować obrazu dokumentu');
                    }}
                />
            );
        } else {
            return (
                <Typography color="textSecondary">
                    Nieobsługiwany typ dokumentu: {documentType}
                </Typography>
            );
        }
    };

    // Status checking for document processing
    useEffect(() => {
        if (!expenseId) return;

        // If we have extracted data already completed, no need to check status
        if (extractedData && extractedData.processing_status === 'completed') {
            return;
        }

        const checkStatus = async () => {
            const status = await checkDocumentStatus();

            if (status === 'completed') {
                // Refresh the page to get the updated data
                window.location.reload();
            } else if (['processing', 'submitted', 'in_progress'].includes(status)) {
                // Continue checking every 3 seconds
                setTimeout(checkStatus, 3000);
            }
        };

        checkStatus();
    }, [expenseId, extractedData]);

    return (
        <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                    <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Podgląd dokumentu
                </Typography>
                {documentType && documentType.startsWith('image/') && (
                    <Box>
                        <IconButton onClick={() => setZoom(prev => Math.min(prev + 0.1, 2))}>
                            <ZoomInIcon />
                        </IconButton>
                        <IconButton onClick={() => setZoom(prev => Math.max(prev - 0.1, 0.5))}>
                            <ZoomOutIcon />
                        </IconButton>
                        <IconButton onClick={() => setRotation(prev => (prev - 90) % 360)}>
                            <RotateLeftIcon />
                        </IconButton>
                        <IconButton onClick={() => setRotation(prev => (prev + 90) % 360)}>
                            <RotateRightIcon />
                        </IconButton>
                    </Box>
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Document processing status display */}
            {extractedData && extractedData.processing_status === 'processing' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Dokument jest przetwarzany przez OCR. To może zająć kilka minut.
                </Alert>
            )}

            <Box
                sx={{
                    height: '500px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    overflow: 'hidden'
                }}
            >
                {loading ? (
                    <CircularProgress />
                ) : (
                    renderDocumentPreview()
                )}
            </Box>
        </Paper>
    );
};

export default DocumentPreview;